import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Close, Delete, Search } from '@mui/icons-material';
import TopManagerSelector from 'app/shared-components/TopManagerSelector';
import { getAbsoluteFileUrl } from 'src/app/helpers/utils';
import { useEffect, useState } from 'react';
import useMessage from 'src/app/hooks/useMessage';
import {
  useCreateManagerSecretaryMutation,
  useEditManagerSecretaryMutation,
} from 'src/app/services/resourceApi';
import { useGetListOfStaffsMutation } from 'src/app/services/staffApi';

const TopManagerAddEditDialog = ({ manager, isOpen, onClose }) => {
  const [top, setTop] = useState(null);
  const [secretary, setSecretary] = useState(null);
  const [isTopSelectorVisible, setIsTopSelectorVisible] = useState(false);
  const [isSecretarySearchVisible, setIsSecretarySearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const { showMessage } = useMessage();
  const { t, i18n } = useTranslation('translations');
  const lang = i18n.language === 'ru' ? 'ru' : 'uzb_lat';

  const [createTop, { isLoadin: isCreating }] = useCreateManagerSecretaryMutation();
  const [editTop, { isLoading: isEditing }] = useEditManagerSecretaryMutation();
  const [getListOfStaffs, { isLoading: isSearching }] = useGetListOfStaffsMutation();

  useEffect(() => {
    if (manager) {
      if (manager.managerId) {
        setTop({
          id: manager.managerId,
          fullName: manager.managerName,
          image: manager.managerImage,
        });
      }
      if (manager.secretaryId) {
        setSecretary({
          id: manager.secretaryId,
          fullName: manager.secretaryName,
          image: manager.secretaryImage,
        });
      }
    }
  }, [manager]);
  const onSave = () => {
    if (isCreating || isEditing) return;
    const promise = manager?.managerId
      ? editTop({
          id: manager.id,
          managerId: top.id,
          secretaryId: secretary ? secretary.id : null,
        })
      : createTop({
          managerId: top.id,
          secretaryId: secretary ? secretary.id : null,
        });

    promise
      .unwrap()
      .then(() => {
        showMessage({ message: t('SuccessfullySaved'), variant: 'success' });
        handleDialogClose();
      })
      .catch((err) => {
        showMessage({ message: t('ErrorWhileSaving'), variant: 'error' });
      });
  };
  const handleRemoveTop = () => setTop(null);
  const handleRemoveSecretary = () => setSecretary(null);
  const handleChooseTop = () => setIsTopSelectorVisible(true);
  const handleDialogClose = () => {
    setTop(null);
    setSecretary(null);
    setSearchResults([]);
    setSearchTerm('');
    setIsSecretarySearchVisible(false);
    onClose();
  };
  const handleSetTop = (top) => {
    if (top) {
      setTop({
        id: top.id,
        fullName: top.name,
        imagePath: top.imagePath,
      });
      setIsTopSelectorVisible(false);
    }
  };
  const handleSearchSecretary = (value) => {
    setSearchTerm(value);
    if (value.trim().length > 2) {
      getListOfStaffs({ page: 1, rows: 10, name: value.trim() })
        .unwrap()
        .then((res) => {
          setSearchResults(res?.items ?? []);
        })
        .catch((err) => {
          setSearchResults([]);
        });
    } else {
      setSearchResults([]);
    }
  };
  const handleSelectSecretary = (person) => {
    setSecretary({
      id: person.id,
      fullName: `${person.fullName?.surname ?? ''} ${person.fullName?.name ?? ''} ${
        person.fullName?.patronymic ?? ''
      }`,
      image: person.photo,
    });
    setIsSecretarySearchVisible(false);
    setSearchResults([]);
    setSearchTerm('');
  };

  return (
    <Dialog open={isOpen} onClose={handleDialogClose}>
      <DialogTitle>
        <div className="flex items-center justify-between">
          <div>{t('AddTopManagerAndSecretary')}</div>
          <IconButton onClick={handleDialogClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {/* Top Manager */}
        <div className="mb-24">
          <p className="mb-6">{t('TopManager')}</p>
          {top ? (
            <div className="flex flex-wrap items-center gap-10">
              <Avatar
                src={getAbsoluteFileUrl(top?.image)}
                variant="rounded"
                sx={{ width: 72, height: 72 }}
              />
              <div className="flex items-center">
                <div>{top?.fullName}</div>
                <div className="mx-6">
                  <Delete className="cursor-pointer text-red" onClick={handleRemoveTop} />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <span className=" text-[#2196f3] cursor-pointer" onClick={handleChooseTop}>
                {t('ChooseTopManager')}
              </span>
              {isTopSelectorVisible && (
                <TopManagerSelector label="TopManager" onChange={handleSetTop} />
              )}
            </div>
          )}
        </div>
        {/* Secretary */}
        <div className="mb-16">
          <p className="mb-6">{t('Secretary')}</p>
          {secretary ? (
            <div className="flex flex-wrap items-center gap-10">
              <Avatar
                src={getAbsoluteFileUrl(secretary?.image)}
                variant="circle"
                sx={{ width: 64, height: 64 }}
              />
              <div className="flex items-center">
                <div>{secretary?.fullName}</div>
                <div className="mx-6">
                  <Delete className="cursor-pointer text-red" onClick={handleRemoveSecretary} />
                </div>
              </div>
            </div>
          ) : (
            <>
              {!isSecretarySearchVisible ? (
                <div className="text-center">
                  <span
                    className=" text-[#2196f3] cursor-pointer"
                    onClick={() => setIsSecretarySearchVisible(true)}
                  >
                    {t('ChooseSecretary')}
                  </span>
                </div>
              ) : (
                <div>
                  <TextField
                    fullWidth
                    size="small"
                    value={searchTerm}
                    onChange={(e) => handleSearchSecretary(e.target.value)}
                    placeholder={t('Search')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <List>
                    {isSearching && <Typography>{t('Loading')}...</Typography>}
                    {!isSearching && searchResults.length === 0 && searchTerm.length > 2 && (
                      <Typography>{t('NoOptions')}</Typography>
                    )}
                    {searchResults.map((person) => (
                      <ListItem
                        key={person.id}
                        button
                        onClick={() => handleSelectSecretary(person)}
                      >
                        <ListItemText
                          primary={`${person.fullName?.surname ?? ''} ${
                            person.fullName?.name ?? ''
                          } ${person.fullName?.patronymic ?? ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </div>
              )}
            </>
          )}
        </div>
        {/* Save Button */}
        <div>
          <Button
            disabled={!top || !secretary}
            className="w-full"
            onClick={onSave}
            variant="contained"
            color="secondary"
          >
            {t('Save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TopManagerAddEditDialog;
